import { useCallback } from 'react';
import DOMPurify from 'dompurify';
import { useEnhancedSecurity } from '@/hooks/useEnhancedSecurity';

export const useInputValidation = () => {
  const { validateInputSecurity } = useEnhancedSecurity();
  
  const sanitizeInput = useCallback((input: string): string => {
    return DOMPurify.sanitize(input, { ALLOWED_TAGS: [] });
  }, []);

  const validateEmail = useCallback((email: string): { isValid: boolean; error?: string } => {
    const sanitized = sanitizeInput(email.trim());
    
    if (!sanitized) {
      return { isValid: false, error: 'Email is required' };
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(sanitized)) {
      return { isValid: false, error: 'Please enter a valid email address' };
    }

    if (sanitized.length > 254) {
      return { isValid: false, error: 'Email address is too long' };
    }

    return { isValid: true };
  }, [sanitizeInput]);

  const validatePassword = useCallback((password: string): { isValid: boolean; error?: string } => {
    if (!password) {
      return { isValid: false, error: 'Password is required' };
    }

    if (password.length < 8) {
      return { isValid: false, error: 'Password must be at least 8 characters long' };
    }

    if (password.length > 128) {
      return { isValid: false, error: 'Password is too long' };
    }

    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (!hasUpperCase || !hasLowerCase || !hasNumbers || !hasSpecialChar) {
      return { 
        isValid: false, 
        error: 'Password must contain uppercase, lowercase, number, and special character' 
      };
    }

    return { isValid: true };
  }, []);

  const validatePin = useCallback((pin: string): { isValid: boolean; error?: string } => {
    const sanitized = sanitizeInput(pin);
    
    if (!sanitized) {
      return { isValid: false, error: 'PIN is required' };
    }

    if (!/^\d{4}$/.test(sanitized)) {
      return { isValid: false, error: 'PIN must be exactly 4 digits' };
    }

    return { isValid: true };
  }, [sanitizeInput]);

  const validateCompanyName = useCallback(async (name: string): Promise<{ isValid: boolean; error?: string }> => {
    const sanitized = sanitizeInput(name.trim());
    
    if (!sanitized) {
      return { isValid: false, error: 'Le nom de l\'entreprise est requis' };
    }

    if (sanitized.length < 2) {
      return { isValid: false, error: 'Le nom de l\'entreprise doit contenir au moins 2 caractères' };
    }

    if (sanitized.length > 100) {
      return { isValid: false, error: 'Le nom de l\'entreprise ne peut pas dépasser 100 caractères' };
    }

    // Enhanced server-side validation
    const isSecure = await validateInputSecurity(sanitized, 'company_name', 100);
    if (!isSecure) {
      return { isValid: false, error: 'Le nom de l\'entreprise contient des caractères non autorisés' };
    }

    return { isValid: true };
  }, [sanitizeInput, validateInputSecurity]);

  return {
    sanitizeInput,
    validateEmail,
    validatePassword,
    validatePin,
    validateCompanyName,
  };
};